





























































































































































































import {
  Component,
  Prop,
  Provide,
  ProvideReactive,
  Vue,
  Watch
} from "vue-property-decorator";
import SolidUnderlineButton from "@/components/SolidUnderlineButton.vue";
import BaseModal from "@/components/BaseModal.vue";
import BorderedButton from "@/components/BorderedButton.vue";
import CategoriesDataService, {
  ArticleCount,
  Category,
  CategorySelectorRow,
  CategoryViewDataRow,
  DateRange,
  KeywordsForCategoryRow,
  Subcategory
} from "@/services/CategoriesDataService";
import PrimaryButton from "@/components/PrimaryButton.vue";
import AddCategoryModal from "@/components/AddCategoryModal.vue";
import StateContainer, { State } from "@/components/StateContainer.vue";
import LoadingTable from "@/components/LoadingTable.vue";
import Chip from "@/components/Chip.vue";
import ExpandingTable from "@/components/ExpandingTable.vue";
import {
  categoriesEqual,
  isSubcategory,
  subcategoriesEqual
} from "@/helpers/CategoryHelpers";
import GraphPanel from "@/components/GraphPanel.vue";
import DropdownList from "@/components/DropdownList.vue";
import { GraphPalette } from "@/styles/graph-palette";

@Component({
  components: {
    DropdownList,
    GraphPanel,
    ExpandingTable,
    Chip,
    LoadingTable,
    StateContainer,
    AddCategoryModal,
    PrimaryButton,
    BorderedButton,
    BaseModal,
    SolidUnderlineButton
  },
  inject: []
})
export default class ComparisonTool extends Vue {
  @Prop() private payload?: string;
  @Provide() private graphState = State.okay;
  @Provide() private articleCounts: ArticleCount[] | null = null;
  @Provide() private graphTitle = "Number of articles indexed in FSTA";
  @Provide() private noDataTitle = "No Category or Subcategory Selected";
  @Provide() private noDataSubtitle =
    "Please click on ‘Add Category or Subcategory’ to populate the graph and view tables below";
  @Provide() dateRange = { start: this.dates.minYear, end: this.dates.maxYear };
  @Provide() keywordState = State.loading;
  @Provide() keywords: KeywordsForCategoryRow[] = [];
  @Provide() journalState = State.loading;
  @Provide() journals: CategoryViewDataRow[] = [];
  @Provide() documentState = State.loading;
  @Provide() documents: CategoryViewDataRow[] = [];
  @Provide() languageState = State.loading;
  @Provide() languages: CategoryViewDataRow[] = [];
  @Provide() category: CategorySelectorRow | null = null;

  @ProvideReactive() selectedCategories: Category[] = [];
  @ProvideReactive() selectedSubcategories: Subcategory[] = [];
  @ProvideReactive() categoriesList: Category[] = [];
  @ProvideReactive() subcategoriesList: Subcategory[] = [];
  @Provide() private colors = GraphPalette.full;

  private colorForCategory(index: number) {
    // TODO map correct colors
    const scaledIndex = index % this.colors.length;
    return this.colors[scaledIndex];
  }

  private colorForSubcategory(index: number) {
    // TODO map correct colors
    const scaledIndex = index % this.colors.length;
    return this.colors[scaledIndex];
  }

  get graphData() {
    let keyIndex = 0;
    const cats = this.allGraphData.categories.map(catGraph => {
      const catIndex = this.selectedCategories.findIndex(selected => {
        return categoriesEqual(selected, catGraph.category);
      });
      if (catIndex < 0) {
        return null;
      }
      keyIndex += 1;
      return {
        id: `${keyIndex}-${catGraph.category.id}`,
        title: catGraph.category.field,
        category: catGraph.category,
        color: this.colorForCategory(catIndex),
        data: catGraph.values
          .filter(
            articles =>
              articles.publication_year >= this.dateRange.start &&
              articles.publication_year <= this.dateRange.end
          )
          .map(articles => ({
            date: articles.publication_year,
            value: articles.article_count
          }))
      };
    });
    const subcats = this.allGraphData.subcategories.map(catGraph => {
      const subcatIndex = this.selectedSubcategories.findIndex(selected => {
        return subcategoriesEqual(selected, catGraph.subcategory);
      });
      if (subcatIndex < 0) {
        return null;
      }
      return {
        id: `${keyIndex}-${catGraph.subcategory.id}`,
        title: catGraph.subcategory.field,
        category: catGraph.subcategory,
        color: this.colorForSubcategory(subcatIndex),
        data: catGraph.values
          .filter(
            articles =>
              articles.publication_year >= this.dateRange.start &&
              articles.publication_year <= this.dateRange.end
          )
          .map(articles => ({
            date: articles.publication_year,
            value: articles.article_count
          }))
      };
    });
    return [...cats, ...subcats].filter(cat => cat !== null);
  }

  get articleCount() {
    if (!this.categoryToShow) {
      const categorySum = this.allGraphData.categories.reduce((sum, cat) => {
        return (
          sum +
          cat.values
            .filter(
              articles =>
                articles.publication_year >= this.dateRange.start &&
                articles.publication_year <= this.dateRange.end
            )
            .reduce((innerSum, art) => innerSum + art.article_count, 0)
        );
      }, 0);
      const subcategorySum = this.allGraphData.subcategories.reduce(
        (sum, cat) => {
          return (
            sum +
            cat.values
              .filter(
                articles =>
                  articles.publication_year >= this.dateRange.start &&
                  articles.publication_year <= this.dateRange.end
              )
              .reduce((innerSum, art) => innerSum + art.article_count, 0)
          );
        },
        0
      );
      return categorySum + subcategorySum;
    } else {
      if (isSubcategory(this.categoryToShow)) {
        return this.allGraphData.subcategories
          .filter(cat =>
            subcategoriesEqual(
              this.categoryToShow as Subcategory,
              cat.subcategory
            )
          )[0]
          .values.filter(
            articles =>
              articles.publication_year >= this.dateRange.start &&
              articles.publication_year <= this.dateRange.end
          )
          .reduce((sum, art) => sum + art.article_count, 0);
      } else {
        return this.allGraphData.categories
          .filter(cat =>
            categoriesEqual(this.categoryToShow as Category, cat.category)
          )[0]
          .values.filter(
            articles =>
              articles.publication_year >= this.dateRange.start &&
              articles.publication_year <= this.dateRange.end
          )
          .reduce((sum, art) => sum + art.article_count, 0);
      }
    }
  }

  @Watch("dateRange", { deep: true })
  dateRangeChange(newValue: DateRange) {
    history.replaceState(
      {},
      "",
      this.$route.path +
        "?payload=" +
        encodeURIComponent(
          JSON.stringify({
            ids: this.selectedCategories,
            sids: this.selectedSubcategories,
            dateRange: newValue
          })
        )
    );
  }

  @Watch("selectedCategories")
  categoriesSelectionChanged(newValue: Category[]) {
    history.replaceState(
      {},
      "",
      this.$route.path +
        "?payload=" +
        encodeURIComponent(
          JSON.stringify({
            ids: newValue,
            sids: this.selectedSubcategories,
            dateRange: this.dateRange
          })
        )
    );
  }

  @Watch("selectedSubcategories")
  subcategoriesSelectionChanged(newValue: Subcategory[]) {
    history.replaceState(
      {},
      "",
      this.$route.path +
        "?payload=" +
        encodeURIComponent(
          JSON.stringify({
            ids: this.selectedCategories,
            sids: newValue,
            dateRange: this.dateRange
          })
        )
    );
  }

  private dateList: number[] = [];
  private isAddCategoryVisible = false;

  private allGraphData: {
    categories: { category: Category; values: ArticleCount[] }[];
    subcategories: { subcategory: Subcategory; values: ArticleCount[] }[];
  } = {
    categories: [],
    subcategories: []
  };

  private allAdditionalData!: {
    keywords: {
      categories: { id: Category; values: KeywordsForCategoryRow[] }[];
      subcategories: { id: Subcategory; values: KeywordsForCategoryRow[] }[];
    };
    journals: {
      categories: { id: Category; values: CategoryViewDataRow[] }[];
      subcategories: { id: Subcategory; values: CategoryViewDataRow[] }[];
    };
    documents: {
      categories: { id: Category; values: CategoryViewDataRow[] }[];
      subcategories: { id: Subcategory; values: CategoryViewDataRow[] }[];
    };
    languages: {
      categories: { id: Category; values: CategoryViewDataRow[] }[];
      subcategories: { id: Subcategory; values: CategoryViewDataRow[] }[];
    };
  };

  private additionalDataTotals: {
    keywords: KeywordsForCategoryRow[];
    journals: CategoryViewDataRow[];
    documents: CategoryViewDataRow[];
    languages: CategoryViewDataRow[];
  } = {
    keywords: [],
    journals: [],
    documents: [],
    languages: []
  };

  private categoryToShow: Category | Subcategory | null = null;
  private additionalDataList: (Category | Subcategory)[] = [];
  private get disableDropdown(): boolean {
    return (
      this.keywordState == State.loading ||
      this.journalState == State.loading ||
      this.documentState == State.loading ||
      this.languageState == State.loading
    );
  }

  private openAddCategory() {
    this.isAddCategoryVisible = true;
  }

  private closeAddCategory() {
    this.isAddCategoryVisible = false;
  }

  private applySelected(categories: Category[], subcategories: Subcategory[]) {
    this.isAddCategoryVisible = false;
    this.selectedCategories = categories;
    this.selectedSubcategories = subcategories;
    this.retrieveArticles();
    this.retrieveAdditionalData();
    this.updateAdditionalDataList();
  }

  private chipTapped(chip: Category | Subcategory): void {
    if (isSubcategory(chip)) {
      this.selectedSubcategories = this.selectedSubcategories.filter(
        sub => !(sub.parentId == chip.parentId && sub.id == chip.id)
      );
    } else {
      this.selectedCategories = this.selectedCategories.filter(
        cat => cat.id != chip.id
      );
    }
    this.updateAdditionalDataList();
    this.removeData(chip);
    this.showDataForAll();
  }

  private removeData(chip: Category | Subcategory) {
    if (isSubcategory(chip)) {
      this.allAdditionalData.keywords.subcategories = this.allAdditionalData.keywords.subcategories.filter(
        sub => !subcategoriesEqual(sub.id, chip)
      );
      this.allAdditionalData.journals.subcategories = this.allAdditionalData.journals.subcategories.filter(
        sub => !subcategoriesEqual(sub.id, chip)
      );
      this.allAdditionalData.documents.subcategories = this.allAdditionalData.documents.subcategories.filter(
        sub => !subcategoriesEqual(sub.id, chip)
      );
      this.allAdditionalData.languages.subcategories = this.allAdditionalData.languages.subcategories.filter(
        sub => !subcategoriesEqual(sub.id, chip)
      );
      this.allGraphData.subcategories = this.allGraphData.subcategories.filter(
        sub => !subcategoriesEqual(sub.subcategory, chip)
      );
    } else {
      this.allAdditionalData.keywords.categories = this.allAdditionalData.keywords.categories.filter(
        cat => !categoriesEqual(cat.id, chip)
      );
      this.allAdditionalData.journals.categories = this.allAdditionalData.journals.categories.filter(
        cat => !categoriesEqual(cat.id, chip)
      );
      this.allAdditionalData.documents.categories = this.allAdditionalData.documents.categories.filter(
        cat => !categoriesEqual(cat.id, chip)
      );
      this.allAdditionalData.languages.categories = this.allAdditionalData.languages.categories.filter(
        cat => !categoriesEqual(cat.id, chip)
      );
      this.allGraphData.categories = this.allGraphData.categories.filter(
        cat => !categoriesEqual(cat.category, chip)
      );
    }
    this.recalculateTotals();
  }

  private startDateSelected(year: number) {
    if (year != this.dateRange.start) {
      this.dateRange.start = year;
      this.retrieveAdditionalData();
    }
  }

  private endDateSelected(year: number) {
    if (year != this.dateRange.end) {
      this.dateRange.end = year;
      this.retrieveAdditionalData();
    }
  }

  private recalculateTotals() {
    this.calculateKeywordTotals();
    this.calculateJournalTotals();
    this.calculateDocumentTotals();
    this.calculateLanguageTotals();
  }

  private updateAdditionalDataList() {
    this.additionalDataList = this.selectedCategories.concat(
      this.selectedSubcategories
    );
  }

  private menuItemSelected(item: Category | Subcategory | null) {
    item ? this.showDataFor(item) : this.showDataForAll();
  }

  private showDataFor(item: Category | Subcategory) {
    this.categoryToShow = item;
    if (isSubcategory(item)) {
      this.keywords =
        this.allAdditionalData.keywords.subcategories
          .filter(
            cat => cat.id.parentId == item.parentId && cat.id.id == item.id
          )[0]
          ?.values.slice(0, 100) ?? [];
      this.journals =
        this.allAdditionalData.journals.subcategories
          .filter(
            cat => cat.id.parentId == item.parentId && cat.id.id == item.id
          )[0]
          ?.values.slice(0, 100) ?? [];
      this.documents =
        this.allAdditionalData.documents.subcategories
          .filter(
            cat => cat.id.parentId == item.parentId && cat.id.id == item.id
          )[0]
          ?.values.slice(0, 100) ?? [];
      this.languages =
        this.allAdditionalData.languages.subcategories
          .filter(
            cat => cat.id.parentId == item.parentId && cat.id.id == item.id
          )[0]
          ?.values.slice(0, 100) ?? [];
    } else {
      this.keywords =
        this.allAdditionalData.keywords.categories
          .filter(cat => cat.id.id == item.id)[0]
          ?.values.slice(0, 100) ?? [];
      this.journals =
        this.allAdditionalData.journals.categories
          .filter(cat => cat.id.id == item.id)[0]
          ?.values.slice(0, 100) ?? [];
      this.documents =
        this.allAdditionalData.documents.categories
          .filter(cat => cat.id.id == item.id)[0]
          ?.values.slice(0, 100) ?? [];
      this.languages =
        this.allAdditionalData.languages.categories
          .filter(cat => cat.id.id == item.id)[0]
          ?.values.slice(0, 100) ?? [];
    }
  }

  private showDataForAll() {
    this.categoryToShow = null;
    this.keywords = this.additionalDataTotals.keywords.slice(0, 100);
    this.documents = this.additionalDataTotals.documents.slice(0, 100);
    this.journals = this.additionalDataTotals.journals.slice(0, 100);
    this.languages = this.additionalDataTotals.languages.slice(0, 100);
  }

  private calculateKeywordTotals() {
    let allValues: any = {};
    [
      ...this.allAdditionalData.keywords.categories
        .map(cat => cat.values)
        .flat(),
      ...this.allAdditionalData.keywords.subcategories
        .map(cat => cat.values)
        .flat()
    ].forEach((i: KeywordsForCategoryRow) => {
      allValues[i.id] = allValues[i.id] || {
        id: i.id,
        field: i.field,
        generic: i.generic,
        amount: 0
      };
      allValues[i.id].amount += i.amount;
    });
    allValues = Object.values(allValues);
    allValues.sort((a: KeywordsForCategoryRow, b: KeywordsForCategoryRow) =>
      a.amount < b.amount ? 1 : b.amount < a.amount ? -1 : 0
    );
    this.additionalDataTotals.keywords = allValues.slice(0, 100);
  }

  private calculateJournalTotals() {
    let allValues: any = {};
    [
      ...this.allAdditionalData.journals.categories
        .map(cat => cat.values)
        .flat(),
      ...this.allAdditionalData.journals.subcategories
        .map(cat => cat.values)
        .flat()
    ].forEach((i: CategoryViewDataRow) => {
      allValues[i.id] = allValues[i.id] || {
        id: i.id,
        field: i.field,
        amount: 0
      };
      allValues[i.id].amount += i.amount;
    });
    allValues = Object.values(allValues);
    allValues.sort((a: CategoryViewDataRow, b: CategoryViewDataRow) =>
      a.amount < b.amount ? 1 : b.amount < a.amount ? -1 : 0
    );
    this.additionalDataTotals.journals = allValues.slice(0, 100);
  }

  private calculateDocumentTotals() {
    let allValues: any = {};
    [
      ...this.allAdditionalData.documents.categories
        .map(cat => cat.values)
        .flat(),
      ...this.allAdditionalData.documents.subcategories
        .map(cat => cat.values)
        .flat()
    ].forEach((i: CategoryViewDataRow) => {
      allValues[i.id] = allValues[i.id] || {
        id: i.id,
        field: i.field,
        amount: 0
      };
      allValues[i.id].amount += i.amount;
    });
    allValues = Object.values(allValues);
    allValues.sort((a: CategoryViewDataRow, b: CategoryViewDataRow) =>
      a.amount < b.amount ? 1 : b.amount < a.amount ? -1 : 0
    );
    this.additionalDataTotals.documents = allValues.slice(0, 100);
  }

  private calculateLanguageTotals() {
    let allValues: any = {};
    [
      ...this.allAdditionalData.languages.categories
        .map(cat => cat.values)
        .flat(),
      ...this.allAdditionalData.documents.subcategories
        .map(cat => cat.values)
        .flat()
    ].forEach((i: CategoryViewDataRow) => {
      allValues[i.id] = allValues[i.id] || {
        id: i.id,
        field: i.field,
        amount: 0
      };
      allValues[i.id].amount += i.amount;
    });
    allValues = Object.values(allValues);
    allValues.sort((a: CategoryViewDataRow, b: CategoryViewDataRow) =>
      a.amount < b.amount ? 1 : b.amount < a.amount ? -1 : 0
    );
    this.additionalDataTotals.languages = allValues.slice(0, 100);
  }

  retrieveCategoryData() {
    CategoriesDataService.getCategoryList()
      .then(response => {
        this.categoriesList = response.data;
      })
      .catch(e => {
        console.log(e);
      });
    CategoriesDataService.getSubcategoryList()
      .then(response => {
        this.subcategoriesList = response.data;
      })
      .catch(e => {
        console.log(e);
      });
  }

  retrieveArticles() {
    this.graphState = State.loading;
    CategoriesDataService.getArticlesComparison(
      this.selectedCategories,
      this.selectedSubcategories
    )
      .then(response => {
        this.allGraphData = {
          categories: response.data.categories.map(cat => {
            return {
              category: cat.category,
              values: cat.values
            };
          }),
          subcategories: response.data.subcategories.map(cat => {
            return {
              subcategory: cat.subcategory,
              values: cat.values
            };
          })
        };
        this.graphState = State.okay;
      })
      .catch(error => {
        this.graphState = State.error;
        console.log(error);
      });
  }

  retrieveAdditionalData() {
    this.keywordState = State.loading;
    this.journalState = State.loading;
    this.documentState = State.loading;
    this.languageState = State.loading;

    if (
      this.selectedCategories.length === 0 &&
      this.selectedSubcategories.length === 0
    ) {
      // TODO show empty state
      return;
    }
    this.allAdditionalData = {
      keywords: {
        categories: [],
        subcategories: []
      },
      documents: {
        categories: [],
        subcategories: []
      },
      journals: {
        categories: [],
        subcategories: []
      },
      languages: {
        categories: [],
        subcategories: []
      }
    };

    CategoriesDataService.getKeywordComparison(
      this.selectedCategories,
      this.selectedSubcategories,
      this.dateRange
    )
      .then(response => {
        this.allAdditionalData.keywords = {
          categories: response.data.categories.map(cat => {
            return {
              id: cat.category,
              values: cat.values
            };
          }),
          subcategories: response.data.subcategories.map(cat => {
            return {
              id: cat.subcategory,
              values: cat.values
            };
          })
        };
        this.calculateKeywordTotals();
        this.showDataForAll();
        this.keywordState = State.okay;
      })
      .catch(error => {
        this.keywordState = State.error;
        console.log(error);
      });

    CategoriesDataService.getJournalsComparison(
      this.selectedCategories,
      this.selectedSubcategories,
      this.dateRange
    )
      .then(response => {
        this.allAdditionalData.journals = {
          categories: response.data.categories.map(cat => {
            return {
              id: cat.category,
              values: cat.values
            };
          }),
          subcategories: response.data.subcategories.map(cat => {
            return {
              id: cat.subcategory,
              values: cat.values
            };
          })
        };
        this.calculateJournalTotals();
        this.showDataForAll();
        this.journalState = State.okay;
      })
      .catch(error => {
        this.journalState = State.error;
        console.log(error);
      });

    CategoriesDataService.getDocumentTypesComparison(
      this.selectedCategories,
      this.selectedSubcategories,
      this.dateRange
    )
      .then(response => {
        this.allAdditionalData.documents = {
          categories: response.data.categories.map(cat => {
            return {
              id: cat.category,
              values: cat.values
            };
          }),
          subcategories: response.data.subcategories.map(cat => {
            return {
              id: cat.subcategory,
              values: cat.values
            };
          })
        };
        this.calculateDocumentTotals();
        this.showDataForAll();
        this.documentState = State.okay;
      })
      .catch(error => {
        this.documentState = State.error;
        console.log(error);
      });

    CategoriesDataService.getLanguagesComparison(
      this.selectedCategories,
      this.selectedSubcategories,
      this.dateRange
    )
      .then(response => {
        this.allAdditionalData.languages = {
          categories: response.data.categories.map(cat => {
            return {
              id: cat.category,
              values: cat.values
            };
          }),
          subcategories: response.data.subcategories.map(cat => {
            return {
              id: cat.subcategory,
              values: cat.values
            };
          })
        };
        this.calculateLanguageTotals();
        this.showDataForAll();
        this.languageState = State.okay;
      })
      .catch(error => {
        this.languageState = State.error;
        console.log(error);
      });
  }

  get dates() {
    return this.$store.state.config.dates;
  }

  mounted() {
    this.retrieveCategoryData();
    const payload = this.$route.query.payload;
    if (payload && typeof payload === "string") {
      const initial = JSON.parse(payload);
      this.dateRange = initial.dateRange ?? {
        start: this.dates.minYear,
        end: this.dates.maxYear
      };
      this.applySelected(initial.ids ?? [], initial.sids ?? []);
    }

    for (let i = this.dates.minYear; i <= this.dates.maxYear; i++) {
      this.dateList.push(i);
    }
  }

  hideGenericKeywords(element: KeywordsForCategoryRow) {
    return !element.generic;
  }
}
